/**
 * Profile component module.
 * @module investor/pages/profile
 *
 */

import React, {useState, useEffect} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import debug from 'debug';
import {
  Address,
  Column,
  DatePicker,
  Dropdown,
  MultiInput,
  Page,
  Row,
  TextInput,
  Tile,
  LoadingSpinner,
} from '../../shared';
import StickyHeaderBar from '../shared/components/StickyHeaderBar';
import {_openModal, _changeEmail} from '../../shared/store';
import {_setNotification} from '../../shared/reducers/headerNotification';
import moment from 'moment';
import clone from 'clone';
import styled from 'styled-components/macro';
import axiosInstance from '../../shared/utils/axios';
import {genders, parseErrorMessage} from '../../shared/utils/utils';

const log = debug('circleblack:profile');

const _Form = styled.form`
  width: 100%;
`;

const Profile = props => {
  const {session, openModal, changeEmail, setNotification} = props;
  const [profile, setProfile] = useState();
  const [isMultiNotControlled, setIsMultiNotControlled] = useState(true); // currently this state is not necessary but left for future use for adding form reset functionality

  const submit = event => {
    event.preventDefault();
    return axiosInstance.put(`households/${session.profile.id}/profile`, profile)
      .then(() => {
        changeEmail({...session.profile, ...profile});
        setNotification({message: 'Profile info has been successfully updated.'});
      })
      .catch(err => setNotification({message: parseErrorMessage(err)}));
  };
  const updateProfile = (name, value) => {
    setProfile(current => ({
      ...current,
      [name]: value,
    }));
  };

  const updateClient = (name, value, idx) => {
    setProfile(current => {
      const tempClient = {...current.clients[idx], [name]: value};
      const updatedClients = [...current.clients];
      updatedClients[idx] = tempClient;
      return {...current, clients: updatedClients};
    });
  };

  useEffect(() => {
    axiosInstance.get(`households/${session.profile.id}/profile`)
      .then(function houseHold(res) {
        setProfile(clone(res.data));
      })
      .catch(err => openModal({
        type: 'Error',
        title: 'Failed to get profile',
        err: err,
      }));
  }, []);

  const householdDetails = () => <Tile key='userProfile' title={'HOUSEHOLD DETAILS'}>
    <Row wrap={true} justifySubRow={'space between'}>
      <TextInput title={'Household Name'}
                 type={'text'}
                 placeholder={'Household Name…'}
                 dataPoint={{
                   name: 'name',
                   value: profile.name,
                 }}
                 onChange={updateProfile}
                 required={true}
                 layout={{grow: 1}}
                 width={'300px'}
                 disabled={!!session.runAsInvestor}/>
      <TextInput title={'Household Phone'}
                 type={'phone'}
                 placeholder={'(###) ###-####'}
                 dataPoint={{
                   name: 'phone',
                   value: profile.phone,
                 }}
                 onChange={updateProfile}
                 layout={{grow: 1}}
                 width={'300px'}
                 disabled={!!session.runAsInvestor}/>
      <TextInput title={'Household Email'}
                 type={'email'}
                 placeholder={'Email…'}
                 dataPoint={{
                   name: 'email',
                   value: profile.email,
                 }}
                 onChange={updateProfile}
                 required={true}
                 layout={{grow: 1}}
                 width={'300px'}
                 disabled={!!session.runAsInvestor}/>
    </Row>
  </Tile>;
  const clientDetails = (client, idx) => <Tile key={'userProfile' + client.id}
                                               title={client.relationship === 'Head'
                                                 ? 'HEAD OF HOUSEHOLD'
                                                 : client?.relationship?.toUpperCase() || 'OTHER'}>
    <Row wrap={true} justifySubRow={'space between'} alignItems={'top'}>
      <Column width={'350px'} layout={{grow: 1}}>
        <TextInput title={'First Name'}
                   type={'text'}
                   placeholder={'First Name…'}
                   dataPoint={{
                     name: 'firstName',
                     value: client.firstName,
                   }}
                   onChange={(name, val) => updateClient(name, val, idx)}
                   required={true}
                   layout={{grow: 1}}
                   width={'fill'}
                   disabled={!!session.runAsInvestor}/>
        <TextInput title={'Middle Name'}
                   type={'text'}
                   placeholder={'Middle Name…'}
                   dataPoint={{
                     name: 'middleName',
                     value: client.middleName,
                   }}
                   onChange={(name, val) => updateClient(name, val, idx)}
                   layout={{grow: 1}}
                   width={'fill'}
                   disabled={!!session.runAsInvestor}/>
        <TextInput title={'Last Name'}
                   type={'text'}
                   placeholder={'Last Name…'}
                   dataPoint={{
                     name: 'lastName',
                     value: client.lastName,
                   }}
                   onChange={(name, val) => updateClient(name, val, idx)}
                   required={true}
                   layout={{grow: 1}}
                   width={'fill'}
                   disabled={!!session.runAsInvestor}/>
        <MultiInput
          title={'Email'}
          InputComponent={TextInput}
          dataPoint={{name: 'emails', value: client.emails}}
          onChange={(name, val) => updateClient(name, val, idx)}
          componentProps={{
            type: 'Email',
            placeholder: 'Email...',
            layout: {grow: 1},
            isNotControlled: isMultiNotControlled,
            onFocus: () => setIsMultiNotControlled(true),
            disabled: !!session.runAsInvestor,
          }}
          dataPropName={'address'}
          emptyValue={{primary: 1, label: '', address: ''}}
          layout={{grow: 1}}
          hasPrimary
          readOnly={!!session.runAsInvestor}
        />
        <MultiInput
          title={'Phone'}
          InputComponent={TextInput}
          dataPoint={{name: 'phones', value: client.phones}}
          onChange={(name, val) => updateClient(name, val, idx)}
          componentProps={{
            type: 'phone',
            placeholder: 'Phone...',
            layout: {grow: 1},
            isNotControlled: isMultiNotControlled,
            onFocus: () => setIsMultiNotControlled(true),
            disabled: !!session.runAsInvestor,
          }}
          dataPropName={'number'}
          emptyValue={{primary: 1, label: '', number: '', type: 'Other', extension: null}}
          layout={{grow: 1}}
          hasPrimary
          readOnly={!!session.runAsInvestor}
        />
      </Column>
      <Column width={'350px'} layout={{grow: 1}}>
        <DatePicker title={'Date of Birth'}
                    readOnly={!'editing'}
                    onChange={(name, val) => updateClient(name, val, idx)}
                    placeholder={'Birth Date'}
                    dataPoint={{name: 'birthDate', value: client.birthDate || ''}}
                    minDate={moment().subtract(100, 'y').format('YYYY-MM-DD')}
                    maxDate={moment().format('YYYY-MM-DD')}
                    width={'fill'}
                    disabled={!!session.runAsInvestor}/>
        <Dropdown title={'Gender'}
                  width={'fill'}
                  placeholder={'Unspecified'}
                  isClearable={true}
                  isSearchable={false}
                  dataPoint={{name: 'gender'}}
                  layout={{grow: 1}}
                  defaultValue={genders.filter(g => g === client.gender).map(g => ({label: g, value: g}))[0]}
                  options={genders.map(g => ({label: g, value: g}))}
                  readOnly={!'editing'}
                  onChange={(name, val) => updateClient(name, val ? val.value : undefined, idx)}
                  disabled={!!session.runAsInvestor}/>
        <MultiInput
          title={'Address'}
          InputComponent={Address}
          dataPoint={{name: 'addresses', value: client.addresses}}
          onChange={(name, val) => updateClient(name, val, idx)}
          componentProps={{
            layout: {grow: 1},
            isNotControlled: isMultiNotControlled,
            onFocus: () => setIsMultiNotControlled(true),
            disabled: !!session.runAsInvestor,
          }}
          emptyValue={{
            primary: 1,
            label: '',
            address: '',
            city: '',
            state: '',
            zip: '',
            zip_4: '',
            country: 'United States',
            type: 'Other',
          }}
          width={'fill'}
          layout={{grow: 1}}
          hasPrimary
          readOnly={!!session.runAsInvestor}
        />
      </Column>
    </Row>
  </Tile>;

  const buttons = {
    primary: {
      label: 'Save Changes',
      form: 'ProfileForm',
      action: 'SUBMIT',
      isDisabled: !!session.runAsInvestor,
    },
    secondary: {
      label: 'Change Password',
      action: () => openModal({type: 'ChangePasswordModal'}),
      isDisabled: !!session.runAsInvestor,
    },
  };
  log('rendering');
  if (!profile) return <LoadingSpinner/>;
  return <Page>
    <_Form id={'ProfileForm'} onSubmit={submit}>
      <StickyHeaderBar title={'PROFILE'} height={'50px'} buttons={buttons}/>
      <Column>
        {householdDetails()}
        {profile.clients && profile.clients.map((client, idx) => clientDetails(client, idx))}
      </Column>
    </_Form>
  </Page>;
};

const mapStateToProps = state => ({
  session: state.session,
});
const mapDispatchToProps = dispatch => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
  setNotification: notification => dispatch(_setNotification(notification)),
  changeEmail: profile => dispatch(_changeEmail(profile)),
});
export default connect(mapStateToProps, mapDispatchToProps)(Profile);

Profile.propTypes = {
  changeEmail: PropTypes.func.isRequired,
  setNotification: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  session: PropTypes.object.isRequired,
};
